import React from 'react';

import ImageHandler from '../ImageHandler';
import { Text } from '../Text';
import { TimelineItem } from './index';
import * as Styles from './Timeline.module.scss';
import { groupByDecade } from './Timeline.utils';

interface TimelineProps {
  grouping?: 'DECADE';
  items: TimelineItem[];
}

interface TimelineGroupProps {
  label: string;
  items: TimelineItem[];
}

interface TimelineEntryProps {
  year: number;
  value: string | React.ReactElement;
  image?: StaticImageDef | React.ReactElement | null;
}

const TimelineEntry: React.FC<TimelineEntryProps> = ({
  year,
  value,
  image,
}) => {
  return (
    <div className={Styles.TimelineGroup}>
      <div className={Styles.TimelineBox}>
        <div className={Styles.TimelineYearPointer}>
          <Text as="b">{year}</Text>
        </div>
        <div className={Styles.TimelineItem}>
          {image && <ImageHandler image={image} />}
          {typeof value === 'string' ? <Text>{value}</Text> : value}
        </div>
      </div>
    </div>
  );
};

const TimelineGroup: React.FC<TimelineGroupProps> = ({ label, items }) => {
  return (
    <>
      <div className={Styles.TimelineGroup}>
        <div className={Styles.TimelineYearPointer}>
          <Text as="b">{label}</Text>
        </div>
      </div>
      {items.map((item, index) => (
        <TimelineEntry
          key={index}
          year={item.year}
          value={item.value}
          image={item.image}
        />
      ))}
    </>
  );
};

const Timeline: React.FC<TimelineProps> = ({ grouping = 'DECADE', items }) => {
  const groupedItems = React.useMemo(() => {
    if (grouping === 'DECADE') {
      return groupByDecade(items);
    }
    throw new Error(`Unsupported grouping type ${grouping}`);
  }, [grouping, items]);

  return (
    <div className={Styles.Timeline}>
      {groupedItems.map((group, index) => (
        <TimelineGroup key={index} label={group.label} items={group.items} />
      ))}
    </div>
  );
};

export default Timeline;
